import { type PointerEvent, useEffect, useRef, useState } from "react";
import { Button } from "@/components/base/buttons/button";

export const SIGNATURE_FILE_NAME = "signature.png";
export const SIGNATURE_FILE_TYPE = "image/png";

export function signatureFileFromBlob(blob: Blob): File {
	return new File([blob], SIGNATURE_FILE_NAME, { type: SIGNATURE_FILE_TYPE });
}

/** Copies the current ink, then changes the bitmap size. Assigning width clears the canvas. */
export function resizeSignatureCanvas(canvas: HTMLCanvasElement): void {
	const rect = canvas.getBoundingClientRect();
	if (rect.width <= 0 || rect.height <= 0) return;
	const dpr = window.devicePixelRatio || 1;
	const nextWidth = Math.floor(rect.width * dpr);
	const nextHeight = Math.floor(rect.height * dpr);
	if (canvas.width === nextWidth && canvas.height === nextHeight) return;

	const ink = document.createElement("canvas");
	ink.width = canvas.width;
	ink.height = canvas.height;
	const inkContext = ink.getContext("2d");
	if (inkContext && canvas.width > 0 && canvas.height > 0) {
		inkContext.drawImage(canvas, 0, 0);
	}

	canvas.width = nextWidth;
	canvas.height = nextHeight;
	const context = canvas.getContext("2d");
	if (!context) return;
	context.setTransform(dpr, 0, 0, dpr, 0, 0);
	context.lineWidth = 2;
	context.lineCap = "round";
	context.lineJoin = "round";
	context.strokeStyle = "#111827";
	context.fillStyle = "#111827";
	if (ink.width > 0 && ink.height > 0) {
		context.drawImage(ink, 0, 0, rect.width, rect.height);
	}
}

export interface SignaturePadProps {
	/** Form-editor preview is a placeholder and does not capture. */
	interactive: boolean;
	placeholder?: string;
	clearLabel?: string;
	disabled?: boolean;
	imageUrl?: string | null;
	onCapture?: (file: File) => void;
	onClear?: () => void;
}

export function SignaturePad({
	interactive,
	placeholder = "Signature is captured when this form is filled in",
	clearLabel = "Clear",
	disabled,
	imageUrl,
	onCapture,
	onClear,
}: SignaturePadProps) {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const drawing = useRef(false);
	const hasStroke = useRef(false);
	const [canClear, setCanClear] = useState(false);
	const [hasLocalInk, setHasLocalInk] = useState(false);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas || !interactive) return;
		const resize = () => {
			resizeSignatureCanvas(canvas);
		};
		resize();
		const observer = new ResizeObserver(resize);
		observer.observe(canvas);
		return () => observer.disconnect();
	}, [interactive]);

	if (!interactive) {
		return (
			<div className="flex h-40 w-full items-center justify-center rounded-lg bg-transparent px-4 text-center text-sm text-tertiary ring-1 ring-primary ring-inset">
				{placeholder}
			</div>
		);
	}

	const pointFromEvent = (event: PointerEvent<HTMLCanvasElement>) => {
		const rect = event.currentTarget.getBoundingClientRect();
		return {
			x: event.clientX - rect.left,
			y: event.clientY - rect.top,
		};
	};

	const finishStroke = () => {
		if (!drawing.current) return;
		drawing.current = false;
		const canvas = canvasRef.current;
		if (!canvas || !hasStroke.current) return;
		const upload = () => {
			canvas.toBlob((blob) => {
				if (!blob) return;
				onCapture?.(signatureFileFromBlob(blob));
			}, SIGNATURE_FILE_TYPE);
		};
		if (!imageUrl || hasLocalInk) {
			setHasLocalInk(true);
			upload();
			return;
		}
		const underlay = new Image();
		underlay.onload = () => {
			const context = canvas.getContext("2d");
			if (!context) return;
			const rect = canvas.getBoundingClientRect();
			const strokes = canvas.toDataURL();
			context.clearRect(0, 0, rect.width, rect.height);
			context.drawImage(underlay, 0, 0, rect.width, rect.height);
			const overlay = new Image();
			overlay.onload = () => {
				context.drawImage(overlay, 0, 0, rect.width, rect.height);
				setHasLocalInk(true);
				upload();
			};
			overlay.src = strokes;
		};
		underlay.src = imageUrl;
	};

	const showSavedImage = Boolean(imageUrl) && !hasLocalInk;

	return (
		<div className="flex flex-col gap-2">
			<div className="relative h-40 w-full">
				{showSavedImage && imageUrl ? (
					<img
						src={imageUrl}
						alt=""
						className="pointer-events-none absolute inset-0 h-full w-full rounded-lg"
					/>
				) : null}
				<canvas
					ref={canvasRef}
					className="absolute inset-0 h-full w-full touch-none rounded-lg bg-transparent ring-1 ring-primary ring-inset"
					onPointerDown={(event) => {
					if (disabled) return;
					const canvas = canvasRef.current;
					const context = canvas?.getContext("2d");
					if (!context) return;
					event.currentTarget.setPointerCapture(event.pointerId);
					const point = pointFromEvent(event);
					context.beginPath();
					context.arc(point.x, point.y, 1, 0, Math.PI * 2);
					context.fill();
					context.beginPath();
					context.moveTo(point.x, point.y);
					drawing.current = true;
					hasStroke.current = true;
					setCanClear(true);
				}}
				onPointerMove={(event) => {
					if (!drawing.current || disabled) return;
					const context = canvasRef.current?.getContext("2d");
					if (!context) return;
					const point = pointFromEvent(event);
					context.lineTo(point.x, point.y);
					context.stroke();
				}}
				onPointerUp={finishStroke}
				onPointerCancel={finishStroke}
			/>
			</div>
			<Button
				size="sm"
				color="secondary"
				type="button"
				isDisabled={disabled || (!imageUrl && !canClear)}
				onClick={() => {
					const canvas = canvasRef.current;
					const context = canvas?.getContext("2d");
					if (canvas && context) {
						context.clearRect(0, 0, canvas.width, canvas.height);
					}
					drawing.current = false;
					hasStroke.current = false;
					setHasLocalInk(false);
					setCanClear(false);
					onClear?.();
				}}
			>
				{clearLabel}
			</Button>
		</div>
	);
}
